/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - European Union 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [headlines.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 30.08.18 13:17
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import LightboxGallery from '../../../views/gallery-view/lightbox-gallery';
import {asyncFetchSpaceMediaByTypePaged, PUBLIC_ACCESS} from '../../../actions/spaces';
import Widget from "../../widgets/widget";
import {useAuthorization, useGalleryChanges, useGenericSpaces, useHomeData} from "../../../selectors";
import {formatFns, useLanguage} from "../../configuration/configuration";
import {
    AddIcon,
    CakeIcon, fs_bigger, fs_regular,
    GalleryIcon,
    LocationIcon,
    MegaphoneIcon,
    PersonEditIcon
} from "../../navigation/icons/nav-icons";
import {RoundedButton, Tooltip} from "../../navigation/buttons/nav-buttons";
import UpdateStatusPopover from "../../dialogs/user/update-status-popover";
import {Popover} from "../../navigation/popover/popover";
import {PENDING_STATUS, SUCCESS_STATUS} from "../../../actions";
import {useHistory} from "react-router-dom";
import {NavigationGroup, NavigationRow} from "../../navigation/layout/nav-layout";
import useFetchGallery from "../../hooks/use-fetch-gallery";
import EditGalleryPopover from "../../popover/edit-gallery-popover";
import GalleryEntrySpace, {GalleryBoxedSpace} from "../../billboard-sidebar/lists/gallery-entry-space";

// TODO ACHTUNG - refactor to let server send preview galleries only --
const MAX_GALLERIES = 8;

const HeadlinesHome = ({username, spaceId}) => {
    const [showMediaGallery, setShowMediaGallery] = useState({isOpen: false, index: 0});
    const [status, setStatus] = useState(PENDING_STATUS);
    const mediaGalleryRef = useRef(null);
    const mediaUrlsRef = useRef([]);
    const {isAuthorized, authorization, isTransitioning} = useAuthorization();
    const {userdata, user, isOwner} = useHomeData();
    const {media, widgets} = useGenericSpaces();
    const {fetchGallerySpaces, spaces: gallerySpaces, status: galleryStatus} = useFetchGallery();
    const dispatch = useDispatch();
    const history = useHistory();
    const {spaceId: galleryId} = useGalleryChanges();
    const Lang = useLanguage();

    const fetchMedia = useCallback((username, spaceId) => {
        setStatus(PENDING_STATUS);
        dispatch(asyncFetchSpaceMediaByTypePaged(username, spaceId,
            'PICTURE', 0, 16, data => {
            mediaUrlsRef.current = [];
            setStatus(SUCCESS_STATUS);
        }));
    }, []);

    useEffect(() => {
        fetchMedia(username, spaceId);
        fetchGallerySpaces(username);
    }, [username, spaceId, galleryId]);

    const handleShowMediaGallery = (isOpen, index) => {
        setShowMediaGallery({isOpen: isOpen, index: index})
    }

    const renderTopWidgets = (widgets) => {
        if (!widgets) return null;
        return widgets.filter(widget => widget.pos === 'LTOP')
            .map(widget => {
                return <Widget key={widget.id} widget={widget} authorization={authorization} mode='LEFT'/>
            })
    }

    const renderBottomWidgets = (widgets) => {
        if (!widgets) return null;
        return widgets.filter(widget => widget.pos === 'LBOTTOM')
            .map(widget => {
                return <Widget key={widget.id} widget={widget} authorization={authorization} mode='LEFT'/>
            })
    }

    const fakeMedia = gallery => {
        return {url: gallery.url, type: 'PICTURE'}
    }

    const renderGallerySpaces = () => {
        const galleries = gallerySpaces.slice(0, MAX_GALLERIES)
        .map(gallery => {
            return <li key={gallery.spaceId} className='list-unstyled mt-1 mb-2'>
                <GalleryEntrySpace space={gallery} size='medium' text={gallery.description}/>
            </li>
        })
        return <ul className='list-unstyled'>{galleries}</ul>
    }

    // close is a callback from popover component
    const handleStatusPopover = ({close}) => {
        return <div>
            <UpdateStatusPopover user={user} onClose={close}/>
        </div>
    }

    // close is a callback from popover component
    // creates a gallery for a home spce (default)
    const handleGalleryPopover = ({close}) => {
        return <div>
            <EditGalleryPopover onClose={close}/>
        </div>
    }

    const handleOnShowGallery = event => {
        event.preventDefault();
        history.push(`/${username}/gallery/home`);
    }


    if (status === PENDING_STATUS || isTransitioning) return null;
    const birthday = formatFns(new Date(userdata.birthday), 'd MMMM');

    return (
        <div className='headlines-container'>
            <div className='generic-navigation-information'>
                <div>
                    <MegaphoneIcon size={fs_bigger} className="mr-2"/>
                    <span className='generic-user-quote-text'>{userdata.status}</span>
                </div>

                {isAuthorized && isOwner && <div className='d-flex justify-content-end'>
                    <Tooltip title={Lang.user.updateStatus}>
                        <Popover className='popover-status-container'
                                 placement='bottom'
                                 render={handleStatusPopover}>
                            <RoundedButton className='btn-outline-secondary p-1'>
                                <PersonEditIcon size={fs_regular}/>
                            </RoundedButton>
                        </Popover>
                    </Tooltip></div>}
            </div>

            <div className='generic-navigation-information mb-2 rounded'>
                <div><LocationIcon size={fs_regular} className="mr-2"/>
                    {Lang.user.currentLocation(user.firstname, userdata.address.city)}
                </div>
                <div><CakeIcon size={fs_regular} className="mr-2"/>{Lang.user.hasBirthdayOn(birthday)}</div>
            </div>

            <div className="widget-container">
                {renderTopWidgets(widgets, authorization)}
            </div>

            <NavigationRow className='bg-white rounded'>
                <NavigationGroup>
                    <span className='sidebar-space-title ml-2'>{Lang.gallery.headline.billboard}</span>
                </NavigationGroup>
                <NavigationGroup>
                    <RoundedButton title={Lang.gallery.headline.tooltip}
                                    onClick={handleOnShowGallery}>
                        <GalleryIcon size={fs_regular}/>
                    </RoundedButton>
                </NavigationGroup>
            </NavigationRow>

            <GalleryBoxedSpace media={media}
                                   username={username}
                                   spacename="Home"
                                   spaceId="home"
                                   description={`${username} ${Lang.gallery.headline.billboard}`}
                                   access={PUBLIC_ACCESS}
                                   className="mt-1"/>

            <div className='generic-navigation-information mt-2 rounded'>
                <NavigationRow>
                    <NavigationGroup>
                        <span className='sidebar-space-title'>{Lang.gallery.headline.moreGalleries}</span>
                    </NavigationGroup>
                    {isAuthorized && isOwner && <NavigationGroup>
                        <Tooltip>
                            <Popover className='popover-status-container'
                                     placement='bottom'
                                     render={handleGalleryPopover}>
                                <RoundedButton className='btn-outline-secondary p-1'
                                               title={Lang.gallery.headline.newGallery}>
                                    <AddIcon size={fs_regular}/>
                                </RoundedButton>
                            </Popover>
                        </Tooltip>
                    </NavigationGroup>}
                </NavigationRow>
                {gallerySpaces && gallerySpaces.length > 0 && renderGallerySpaces()}
            </div>

            {showMediaGallery.isOpen &&
                <LightboxGallery media={mediaUrlsRef.current}
                                 ref={mediaGalleryRef}
                                 open={true}
                                 index={showMediaGallery.index}
                                 onClose={() => handleShowMediaGallery(false, 0)}/>
            }
            <div className="widget-container pt-4">
                {renderBottomWidgets(widgets, authorization)}
            </div>
        </div>
    );
}

export default HeadlinesHome;